import React, { useCallback, useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
// Types
interface ProductData {
  title: string;
  price: string;
  image: string;
}
interface NotificationData {
  name: string;
  location: string;
  time: string;
  product: ProductData;
}
// Data pools
const NAMES = ['John', 'Mike', 'Carlos', 'David', 'James', 'Roberto', 'Michael', 'William', 'Richard', 'Joseph', 'Thomas', 'Charles', 'Christopher', 'Daniel', 'Matthew', 'Anthony', 'Mark', 'Donald', 'Steven', 'Paul', 'Andrew', 'Joshua', 'Kenneth', 'Kevin', 'Brian', 'George', 'Edward', 'Ronald', 'Timothy', 'Jason', 'Jeffrey', 'Ryan', 'Jacob', 'Gary', 'Nicholas', 'Eric', 'Jonathan', 'Stephen', 'Larry', 'Justin', 'Scott', 'Brandon', 'Benjamin', 'Samuel', 'Gregory', 'Frank', 'Alexander', 'Raymond', 'Patrick', 'Jack', 'Dennis', 'Jerry', 'Tyler', 'Aaron', 'Jose', 'Adam', 'Henry', 'Nathan', 'Douglas', 'Zachary', 'Peter', 'Kyle', 'Walter', 'Ethan', 'Jeremy', 'Christian', 'Keith', 'Roger', 'Terry', 'Gerald'];
const LOCATIONS = ['Houston, TX', 'Miami, FL', 'Phoenix, AZ', 'Chicago, IL', 'New York, NY', 'Los Angeles, CA', 'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Washington, DC', 'Boston, MA', 'El Paso, TX', 'Nashville, TN', 'Detroit, MI', 'Oklahoma City, OK', 'Portland, OR', 'Las Vegas, NV', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD', 'Milwaukee, WI', 'Albuquerque, NM', 'Tucson, AZ', 'Fresno, CA', 'Mesa, AZ', 'Sacramento, CA', 'Atlanta, GA', 'Kansas City, MO', 'Colorado Springs, CO', 'Raleigh, NC', 'Omaha, NE', 'Long Beach, CA', 'Virginia Beach, VA', 'Oakland, CA', 'Minneapolis, MN', 'Tulsa, OK', 'Arlington, TX', 'Tampa, FL', 'New Orleans, LA', 'Wichita, KS', 'Cleveland, OH', 'Bakersfield, CA', 'Aurora, CO', 'Anaheim, CA', 'Honolulu, HI', 'Santa Ana, CA', 'Riverside, CA'];
const TIMES = ['just now', '1 min ago', '2 mins ago', '3 mins ago', '4 mins ago', '5 mins ago', '7 mins ago', '10 mins ago', '12 mins ago', '15 mins ago'];
// Default product fallback
const DEFAULT_PRODUCT: ProductData = {
  title: 'Erectile Diffusion Guide',
  price: '$6.39',
  image: 'https://cdn.magicpatterns.com/uploads/9RexrV9B7AUBN21YTtYaTV/private_openrouter-generated-image-1773284425761.png'
};
// Helper to get random item from array
const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
// Helper to get random number between min and max
const getRandomInt = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;
export function RecentPurchasePopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenClosed, setHasBeenClosed] = useState(false);
  const [productData, setProductData] = useState<ProductData>(DEFAULT_PRODUCT);
  const [currentNotification, setCurrentNotification] = useState<NotificationData | null>(null);
  // 1. Scan DOM for product data on mount
  useEffect(() => {
    const scanDOM = () => {
      try {
        let title = DEFAULT_PRODUCT.title;
        let price = DEFAULT_PRODUCT.price;
        let image = DEFAULT_PRODUCT.image;
        // Try to find price (look for $X.XX pattern)
        const priceRegex = /\$\s?\d+(\.\d{2})?/;
        const allTextNodes = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
        let node;
        while (node = allTextNodes.nextNode()) {
          if (node.nodeValue && priceRegex.test(node.nodeValue)) {
            const match = node.nodeValue.match(priceRegex);
            if (match && match[0]) {
              // Prefer prices that look like our main price, not crossed out ones
              const parentElement = node.parentElement;
              if (parentElement && !parentElement.className.includes('line-through')) {
                price = match[0];
                break;
              }
            }
          }
        }
        // Try to find title near pricing or hero
        const h1s = document.getElementsByTagName('h1');
        const h2s = document.getElementsByTagName('h2');
        // Look for specific product title if possible, or use the first main heading
        if (h2s.length > 0) {
          for (let i = 0; i < h2s.length; i++) {
            const text = h2s[i].textContent || '';
            if (text.toLowerCase().includes('guide') || text.toLowerCase().includes('manual')) {
              title = text.trim();
              break;
            }
          }
        }
        // Try to find main product image
        const images = document.getElementsByTagName('img');
        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          // Look for hero or product images, avoid small avatars
          if (img.src && img.width > 100 && img.height > 100 && !img.src.includes('avatar') && !img.src.includes('face')) {
            image = img.src;
            break;
          }
        }
        setProductData({
          title,
          price,
          image
        });
      } catch (error) {
        console.error('Error scanning DOM for product data:', error);
        // Fallback to defaults already set in state
      }
    };
    // Small delay to ensure DOM is fully rendered
    setTimeout(scanDOM, 1000);
  }, []);
  // 2. Manage notification cycle
  const generateNewNotification = useCallback(() => {
    setCurrentNotification({
      name: getRandomItem(NAMES),
      location: getRandomItem(LOCATIONS),
      time: getRandomItem(TIMES),
      product: productData
    });
  }, [productData]);
  useEffect(() => {
    if (hasBeenClosed) return;
    let showTimer: NodeJS.Timeout;
    let hideTimer: NodeJS.Timeout;
    const cycleNotification = () => {
      // Generate new data
      generateNewNotification();
      // Show it
      setIsVisible(true);
      // Hide it after ~4 seconds
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        // Schedule next one after 8-12 seconds
        if (!hasBeenClosed) {
          showTimer = setTimeout(cycleNotification, getRandomInt(8000, 12000));
        }
      }, 4000);
    };
    // Initial delay before first notification (5-8 seconds)
    showTimer = setTimeout(cycleNotification, getRandomInt(5000, 8000));
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [hasBeenClosed, generateNewNotification]);
  const handleClose = () => {
    setIsVisible(false);
    setHasBeenClosed(true);
  };
  if (hasBeenClosed || !currentNotification) return null;
  return <div className={`fixed bottom-4 left-4 z-40 w-full max-w-[320px] transition-all duration-500 ease-in-out transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0 pointer-events-none'}`} data-id="element-245">
      <div className="bg-dark-card border border-dark-border border-l-4 border-l-gold rounded-lg shadow-2xl p-3 pr-8 relative overflow-hidden group" data-id="element-246">
        {/* Close button */}
        <button onClick={handleClose} className="absolute top-2 right-2 text-text-secondary hover:text-white transition-colors p-1" aria-label="Close notification" data-id="element-247">
          <XIcon className="w-4 h-4" data-id="element-248" />
        </button>

        <div className="flex items-start gap-3" data-id="element-249">
          {/* Thumbnail */}
          <div className="w-12 h-16 flex-shrink-0 rounded overflow-hidden bg-dark-bg border border-dark-border" data-id="element-250">
            <img src={currentNotification.product.image} alt={currentNotification.product.title} className="w-full h-full object-cover" data-id="element-251" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 flex flex-col justify-center" data-id="element-252">
            <p className="text-[10px] font-bold text-gold uppercase tracking-wider mb-0.5 flex items-center gap-1" data-id="element-253">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-254"></span>
              Someone just purchased!
            </p>

            <h4 className="text-sm font-bold text-text-primary leading-tight mb-1 truncate" data-id="element-255">
              {currentNotification.product.title}
            </h4>

            <p className="text-xs text-text-secondary truncate mb-0.5" data-id="element-256">
              {currentNotification.name} from {currentNotification.location}
            </p>

            <div className="flex items-center justify-between mt-0.5" data-id="element-257">
              <p className="text-[10px] text-text-secondary/70" data-id="element-258">
                {currentNotification.time}
              </p>
              <p className="text-xs font-bold text-gold" data-id="element-259">
                {currentNotification.product.price}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}